/** @format */

import { Component, HostListener, Inject, LOCALE_ID } from "@angular/core";
import { formatDate, Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastService } from "src/app/services/toast/toast.service";
import { DatePipe } from "@angular/common";
import { LogSheetFormService } from "src/app/services/log-sheet-form/log-sheet-form.service";
import { ErrorHandlerService } from "src/app/services/errorHandler/error-handler.service";
import { DataPointService } from "src/app/services/data-point/data-point.service";
import { HttpEvent, HttpEventType } from "@angular/common/http";
import { LogsheetService } from "src/app/services/logsheet/logsheet.service";
import { SubmissionGuardService } from "src/app/services/submission-guard/submission-guard.service";
import { AuthService } from "src/app/services/auth/auth.service";
import { SubmissionGuard } from "src/app/models/submission-guard.model";
import { environment } from "src/environments/environment";
import * as _ from "lodash";
import { Roles } from "src/app/auth/roles";

@Component({
  standalone: false,
  selector: 'app-public-form',
  templateUrl: './public-form.component.html',
  styleUrls: ['./public-form.component.css']
})

export class PublicFormComponent {
  isVersionMenuVisible: boolean = false;
  isNoteMenuVisible: boolean = false;
  selectedDataPointIdx: number = 0;
  selectedSectionIdx: number = 0;
  showNoteModal: boolean = false;
  note: string = "";
  showDeactivateMenu = false;
  activeVersion: number = 0;
  isSubmitting: boolean = false;
  hasSubmitted: boolean = false;
  isDeleting: boolean = false;
  isAddingSection: boolean = false;
  versions: any = [];
  baseUrl: string = environment.apiUrl;
  currentRoute: string = "";
  isSaving: boolean = false;
  currentRole: string = localStorage.getItem('role') ?? '';
  onloadError: string = '';
  onloadErrorType: string = "";

  logSheet: any = {};

  lastModifiedDate = "";
  errorMessage: { success: boolean; message: string } = {
    success: true,
    message: "",
  };

  initialDataPointValues: any = [];
  dataPoints: any = [];
  dataPointValues: any = [];
  currentState: any = [];

  // Section management
  sections: any = [];
  sectionCount: number = 1;
  submittedSectionCount: number = 0; // Track how many sections have been submitted
  nextEntryId: number = 1; // Track the next entry ID to use
  isDynamicForm: boolean = false; // Track if form is dynamic
  deletedEntryIds: number[] = []; // Track deleted entry IDs

  // Guard evaluation
  guards: SubmissionGuard[] = [];
  guardsLoading: boolean = false;
  guardsLoaded: boolean = false;
  guardQueue: SubmissionGuard[] = [];
  activeGuard: SubmissionGuard | null = null;
  activeGuardMessage: string = '';
  pendingSubmitCallback: (() => void) | null = null;

  deactivateText = {
    title: "Delete Note",
    message1: "",
    message2: "",
  };

  errorMsgEmpty = "This field is empty, please input a value or add a note";
  errorMsgNAN = "Please enter a valid number";
  errorMsgMinValue = "Please enter at least 2 characters";
  errorMsgDenominator = "The Denominator cannot be set to zero";
  errorIntegerOnly = "Only integers are accepted";
  errorMsgRange =
    "This entry is out of range. Please revise entry or leave a note.";
  errorMsgInvalidDate = "Invalid date. Use dd-MMM-yyyy format (e.g., 29-Jan-2026). Year must be between 1900-2100.";

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private route: ActivatedRoute,
    private router: Router,
    public toastService: ToastService,
    private logSheetFormService: LogSheetFormService,
    private logSheetService: LogsheetService,
    private errorService: ErrorHandlerService,
    private location: Location,
    private datePipe: DatePipe,
    private guardService: SubmissionGuardService,
    private authService: AuthService,
    private dataPointService: DataPointService
  ) {
    this.currentRoute = this.router.url;
  }

  // File Upload helpers. Config lives in listOptions as "types,maxFiles,maxSizeMB";
  // the value is a ";"-separated list of "fileId|fileName" entries.
  private parseFileCfg(dp: any): { types: string; maxFiles: number; maxSizeMB: number } {
    const cfg = (dp?.['listOptions'] || 'any,1,10').split(',');
    return {
      types: (cfg[0] || 'any').trim(),
      maxFiles: Math.max(1, parseInt(cfg[1], 10) || 1),
      maxSizeMB: Math.max(1, parseInt(cfg[2], 10) || 10),
    };
  }

  fileValueList(value: string): { id: string; name: string }[] {
    if (!value) return [];
    return value.split(';').filter((v) => v).map((part) => {
      const [id, name] = part.split('|');
      return { id, name: name || id };
    });
  }

  // Downloads via HttpClient so the auth interceptor adds the JWT, then saves
  // the returned blob. A plain anchor navigation would hit the endpoint without
  // a token and fail with 401.
  downloadFile(f: { id: string; name: string }): void {
    this.dataPointService.downloadDataPointFile(f.id).subscribe({
      next: (resp) => {
        const blob = resp.body;
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = f.name;
        a.click();
        URL.revokeObjectURL(url);
      },
    });
  }

  fileAccept(dp: any): string {
    const types = this.parseFileCfg(dp).types;
    return types && types.toLowerCase() !== 'any' ? types : '';
  }

  fileAllowsMultiple(dp: any): boolean {
    return this.parseFileCfg(dp).maxFiles > 1;
  }

  // Per-field UI state, keyed by "sectionIdx_idx".
  fileErrors: { [key: string]: string } = {};
  fileUploads: { [key: string]: { name: string; progress: number }[] } = {};

  private fileKey(sectionIdx: number, idx: number): string {
    return `${sectionIdx}_${idx}`;
  }
  fileErrorFor(sectionIdx: number, idx: number): string {
    return this.fileErrors[this.fileKey(sectionIdx, idx)] || '';
  }
  fileUploadsFor(sectionIdx: number, idx: number): { name: string; progress: number }[] {
    return this.fileUploads[this.fileKey(sectionIdx, idx)] || [];
  }
  fileAtMax(dp: any, sectionIdx: number, idx: number, value: string): boolean {
    const used = this.fileValueList(value).length + this.fileUploadsFor(sectionIdx, idx).length;
    return used >= this.parseFileCfg(dp).maxFiles;
  }

  // Validate against the field's type/size/count limits, then upload each file
  // with a live progress bar; failures show an inline error under the field.
  onDataPointFileSelected(event: Event, sectionIdx: number, idx: number, dp: any): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const key = this.fileKey(sectionIdx, idx);
    this.fileErrors[key] = '';
    const cfg = this.parseFileCfg(dp);
    const cell = this.sections[sectionIdx].dataPointValues[idx];
    const allowed = cfg.types.toLowerCase() === 'any'
      ? [] : cfg.types.split(',').map((t) => t.trim().toLowerCase()).filter((t) => t);
    let count = this.fileValueList(cell['value']).length + this.fileUploadsFor(sectionIdx, idx).length;

    for (const file of Array.from(input.files)) {
      if (count >= cfg.maxFiles) { this.fileErrors[key] = `Up to ${cfg.maxFiles} file(s) allowed`; break; }
      if (file.size > cfg.maxSizeMB * 1024 * 1024) { this.fileErrors[key] = `${file.name} exceeds the ${cfg.maxSizeMB}MB limit`; continue; }
      if (allowed.length && !allowed.some((ext) => file.name.toLowerCase().endsWith(ext))) {
        this.fileErrors[key] = `${file.name} is not an allowed file type`; continue;
      }
      count++;
      const entry = { name: file.name, progress: 0 };
      (this.fileUploads[key] = this.fileUploads[key] || []).push(entry);
      this.dataPointService.uploadDataPointFile(file).subscribe({
        next: (ev: HttpEvent<any>) => {
          if (ev.type === HttpEventType.UploadProgress && ev.total) {
            entry.progress = Math.round((ev.loaded / ev.total) * 100);
          } else if (ev.type === HttpEventType.Response) {
            const res = ev.body;
            cell['value'] = (cell['value'] ? cell['value'] + ';' : '') + `${res.dataPointFileId}|${res.fileName}`;
            this.removeFileUpload(key, entry);
            this.onInputChange(sectionIdx);
          }
        },
        error: () => { this.removeFileUpload(key, entry); this.fileErrors[key] = `Upload of ${file.name} failed`; },
      });
    }
    input.value = '';
  }

  private removeFileUpload(key: string, entry: { name: string; progress: number }): void {
    const arr = this.fileUploads[key];
    if (!arr) return;
    const i = arr.indexOf(entry);
    if (i >= 0) arr.splice(i, 1);
  }

  removeDataPointFile(sectionIdx: number, idx: number, fileIdx: number): void {
    const cell = this.sections[sectionIdx].dataPointValues[idx];
    const parts = (cell['value'] || '').split(';').filter((v: string) => v);
    parts.splice(fileIdx, 1);
    cell['value'] = parts.join(';');
    this.onInputChange(sectionIdx);
  }

  // Duration is a self-contained field: its own value holds "start|end" dates and
  // listOptions holds the unit. Returns the elapsed time formatted in that unit,
  // or '' when either date is missing/invalid (so the field shows a placeholder).
  computeDuration(value: string, listOptions: string): string {
    const [start, end] = (value || '').split('|');
    if (!start || !end) return '';
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    if (isNaN(startMs) || isNaN(endMs)) return '';
    const unit = (listOptions || 'days').trim();
    const divisor = unit === 'minutes' ? 60000 : unit === 'hours' ? 3600000 : 86400000;
    const effectiveUnit = unit === 'minutes' || unit === 'hours' ? unit : 'days';
    return `${Math.round(((endMs - startMs) / divisor) * 100) / 100} ${effectiveUnit}`;
  }



  stringToListConverter(list: string) {
    if (list === "" || list == null) {
      return [];
    }
    return list.split(",").map((e: any) => e.trim());
  }

  handleMultiSelect(dataPointIdx: any, option: string, sectionIdx?: number) {
    const dataPointValues = sectionIdx !== undefined
      ? this.sections[sectionIdx].dataPointValues
      : this.dataPointValues;

    let selectedValues = this.stringToListConverter(
      dataPointValues[dataPointIdx]?.value
    );

    if (selectedValues?.indexOf(option) != -1) {
      selectedValues = [
        ...selectedValues?.filter((e: any) => e.trim() != option),
      ];
    } else {
      selectedValues = [...selectedValues, option];
    }
    dataPointValues[dataPointIdx]["value"] = selectedValues?.join(",");
    // Mark section as unsaved when value changes
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].isSubmitted = false;
    }
  }

  // Mark section as unsaved when any input changes
  onInputChange(sectionIdx?: number): void {
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].isSubmitted = false;
    }
  }

  // Date Range values are stored as "start|end". Update one side and rejoin.
  setDateRangePart(sectionIdx: number, idx: number, part: 'start' | 'end', val: string): void {
    const parts = (this.sections[sectionIdx].dataPointValues[idx]['value'] || '|').split('|');
    if (part === 'start') parts[0] = val; else parts[1] = val;
    this.sections[sectionIdx].dataPointValues[idx]['value'] = `${parts[0] || ''}|${parts[1] || ''}`;
    this.onInputChange(sectionIdx);
  }

  // Normalize text: convert CRLF to LF for consistent counting across platforms
  normalizeText(text: string): string {
    if (!text) return '';
    return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  }

  // Count actual user-perceived characters (handles emojis and Unicode correctly)
  countCharacters(text: string | null | undefined): number {
    if (!text) return 0;
    const normalized = this.normalizeText(text);
    // Use Array.from to properly count Unicode graphemes
    return Array.from(normalized).length;
  }

  // Handle LONG_TEXT input changes with normalization
  onLongTextChange(
    value: string,
    sectionIdx: number,
    dataPointIdx: number,
    section: any
  ): void {
    // Normalize line endings for consistent counting
    const normalized = this.normalizeText(value);
    section.dataPointValues[dataPointIdx]['value'] = normalized;
    // Mark section as unsaved
    if (this.isDynamicForm) {
      section.isSubmitted = false;
    }
  }

  isMultiSelected(dataPointIdx: any, option: string, sectionIdx?: number) {
    if (sectionIdx !== undefined && this.activeVersion == 0) {
      return this.stringToListConverter(
        this.sections[sectionIdx].dataPointValues[dataPointIdx]["value"]
      )?.indexOf(option) >= 0;
    }

    if (
      (this.activeVersion == 0 &&
        this.stringToListConverter(
          this.dataPointValues?.[dataPointIdx]["value"]
        )?.indexOf(option) >= 0) ||
      (this.activeVersion > 0 &&
        this.stringToListConverter(
          this.versions?.[dataPointIdx]?.["dataPointValue"][
          this.activeVersion
          ]?.["value"]
        )?.indexOf(option) != -1)
    ) {
      return true;
    }
    return false;
  }

  captureGPS(idx: number, sectionIdx?: number): void {
    if (!navigator.geolocation) {
      this.toastService.showToast = true;
      this.toastService.toastText = "Geolocation is not supported by this browser.";
      setTimeout(() => { this.toastService.showToast = false; }, 5000);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const now = new Date();
        const formattedDate = this.datePipe.transform(now, "dd-MMM-yyyy hh:mm a") || "";
        const lat = position.coords.latitude.toFixed(4);
        const lng = position.coords.longitude.toFixed(4);
        const alt = position.coords.altitude != null
          ? position.coords.altitude.toFixed(2) + " m"
          : "N/A";

        let locationName = "Unknown";
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
            { headers: { "User-Agent": "SmartForms/2.0" } }
          );
          const data = await response.json();
          const addr = data.address || {};
          const locationParts = [
            addr.road,
            addr.suburb || addr.neighbourhood || addr.quarter || addr.village || addr.town || addr.city,
            addr.country,
          ].filter(Boolean);
          locationName = locationParts.join(", ") || "Unknown";
        } catch {
          // fall back silently
        }

        const gpsValue = `${formattedDate} | Lat: ${lat} | Lng: ${lng} | Alt: ${alt} | Location: ${locationName}`;

        if (sectionIdx !== undefined) {
          this.sections[sectionIdx].dataPointValues[idx]["value"] = gpsValue;
          this.onInputChange(sectionIdx);
        } else {
          this.dataPointValues[idx]["value"] = gpsValue;
        }
      },
      (error) => {
        let errorMsg = "Unable to retrieve location.";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = "Location permission denied. Please allow location access in your browser.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMsg = "Location request timed out.";
            break;
        }
        this.toastService.showToast = true;
        this.toastService.toastText = errorMsg;
        setTimeout(() => { this.toastService.showToast = false; }, 5000);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  getGpsField(value: string, field: string): string {
    if (!value) return "";
    const parts = value.split(" | ");
    switch (field) {
      case "time":
        return parts[0] || "";
      case "lat":
        return parts[1]?.replace("Lat: ", "") || "";
      case "lng":
        return parts[2]?.replace("Lng: ", "") || "";
      case "alt":
        return parts[3]?.replace("Alt: ", "") || "";
      case "location":
        return parts[4]?.replace("Location: ", "") || "";
      default:
        return "";
    }
  }

  isSingleSelected(dataPointIdx: any, option: string, sectionIdx?: number) {
    if (sectionIdx !== undefined && this.activeVersion == 0) {
      return this.sections[sectionIdx].dataPointValues[dataPointIdx]["value"]
        .toLowerCase() == option.toLowerCase();
    }

    if (
      (this.activeVersion == 0 &&
        this.dataPointValues?.[dataPointIdx]["value"].toLowerCase() ==
        option.toLowerCase()) ||
      (this.activeVersion > 0 &&
        this.versions?.[dataPointIdx]?.["dataPointValue"][this.activeVersion]?.[
          "value"
        ]
          .toLowerCase()
          .trim() == option.toLowerCase())
    ) {
      return true;
    }
    return false;
  }
  goBack() {
    window.history.back();
  }

  singleSelect: any = {};
  // handleSingleSelect(idx: any, option: any, sectionIdx?: number) {
  //   const dataPointValues = sectionIdx !== undefined 
  //     ? this.sections[sectionIdx].dataPointValues 
  //     : this.dataPointValues;
  //   dataPointValues[idx].value = option.trim();
  // }

  handleSingleSelect(idx: any, option: any, sectionIdx?: number) {
    // Guard: prevent edits on previous versions
    if (this.activeVersion != 0) return;

    // Allow editing for both dynamic and static forms
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx].value = option.trim();
      this.onInputChange(sectionIdx);
      return;
    }
  }

  isChecked(idx: number, option: string): boolean {
    return this.dataPointValues[idx].value === option;
  }

  private initFromLogSheet(logSheet: any): void {
    this.logSheet = logSheet ?? {};


    // Only show Add/Remove section controls for explicitly-dynamic logsheets
    const formType = this.logSheet?.formType || this.logSheet?.logSheetTemplated?.formType;
    this.isDynamicForm =
      (formType ?? "").toString().toLowerCase().trim() === "dynamic";
    const scheduleType: string | undefined =
      this.logSheet?.logSheetTemplated?.scheduleType;
    //Excecuted and Non-Admin - Do not

    // if (scheduleType && scheduleType.toLowerCase() !== "ondemand" && this.currentRole !== Roles.operator) {
    //   this.updateLogsheetStatus("inProgress");
    // } else if (this.logSheet.status !== 'executed' && this.currentRole == 'Operator' && scheduleType && scheduleType.toLowerCase() !== "ondemand") {
    //   this.updateLogsheetStatus("inProgress");
    // }


    this.dataPoints = this.logSheet?.logSheetTemplated?.dataPointDetails ?? [];

    // Load guards for this template
    const templateId = this.logSheet?.logSheetTemplated?.logSheetTemplateId ?? this.logSheet?.logSheetTemplateId;
    if (templateId) {
      this.guardsLoading = true;
      this.guardService.getGuardsForTemplate(templateId).subscribe({
        next: (guards) => {
          this.guards = guards;
          this.guardsLoaded = true;
          this.guardsLoading = false;
        },
        error: () => {
          this.guards = [];
          this.guardsLoaded = true;
          this.guardsLoading = false;
        }
      });
    } else {
      this.guardsLoaded = true;
    }

    // Load all entries from the logsheet
    this.loadAllEntries();

    if ((this.logSheet?.nonAdminEditCount ?? 0) + (this.logSheet?.adminEditCount ?? 0) > 0) {
      this.retrievePreviousVersions();
    }
  }

  ngOnInit(): void {

    // Default to static until we know for sure.
    this.isDynamicForm = false;

    this.authService.setToken(this.route.snapshot.paramMap.get('token') ?? "");

    this.logSheetService.retrieveLogSheetForPublicUser().subscribe({
      next: (response) => {
        this.initFromLogSheet(response.logSheet);
      },
      error: (err) => {
        this.logSheet = null;
        this.authService.clearToken();
        this.onloadError = err.error.message;
        if (err.error.errorType) {
          this.onloadErrorType = err.error.errorType
        }
      }
    })
  }

  loadAllEntries(): void {
    // Check if there are any saved entries in the dataPointValue arrays
    if (this.dataPoints && this.dataPoints.length > 0 && this.dataPoints[0]["dataPointValue"]?.length > 0) {
      // Group dataPointValues by logSheetEntryId to identify separate entries
      const uniqueEntryIds = new Set<number>();
      this.dataPoints.forEach((dp: any) => {
        dp["dataPointValue"].forEach((dpv: any) => {
          if (dpv.logSheetEntryId) {
            uniqueEntryIds.add(dpv.logSheetEntryId);
          }
        });
      });

      const entryIdList = Array.from(uniqueEntryIds).sort((a, b) => a - b);

      // Create sections for each unique entry ID
      this.sections = [];
      for (let entryIdx = 0; entryIdx < entryIdList.length; entryIdx++) {
        const entryId = entryIdList[entryIdx];
        const dataPointValues = [];

        // For each data point, find the value with this entryId
        for (let dp of this.dataPoints) {
          const dpValue = dp["dataPointValue"].find((v: any) => v.logSheetEntryId === entryId);

          dataPointValues.push({
            dataPointId: dp["dataPointId"],
            value: dpValue?.["value"] || "",
            note: dpValue?.["note"] || "",
          });
        }

        this.sections.push({
          id: entryIdx + 1,
          name: `Entry ${entryIdx + 1}`,
          dataPointValues: dataPointValues,
          dataPoints: this.dataPoints,
          isSubmitted: this.currentRoute.includes("/forms") ? true : false, // Mark loaded entries as already submitted
          entryId: entryId,
        });
      }

      this.sectionCount = entryIdList.length;
      this.submittedSectionCount = entryIdList.length;
      this.nextEntryId = entryIdList.length > 0 ? Math.max(...entryIdList) + 1 : 1;

      // Set initial values
      let allDataPointValues: any = [];
      for (let section of this.sections) {
        allDataPointValues = [...allDataPointValues, ...section.dataPointValues];
      }
      this.initialDataPointValues = JSON.parse(JSON.stringify(allDataPointValues));
      this.dataPointValues = allDataPointValues;
    } else {
      // No existing entries, create one empty section
      this.initialDataPointValues = this.getDataPointValues(this.dataPoints);
      this.dataPointValues = this.getDataPointValues(this.dataPoints);
      this.initializeSections();
    }

    // Safety check: ensure sections array is never empty
    if (!this.sections || this.sections.length === 0) {
      console.warn('Sections array is empty! Force initializing...');
      this.initialDataPointValues = this.getDataPointValues(this.dataPoints);
      this.dataPointValues = this.getDataPointValues(this.dataPoints);
      this.initializeSections();
    }

    // Populate auto fields up front so they're visible while filling the form.
    // (Submitted-At is re-stamped at submit, so this is just a live preview.)
    this.stampReferenceNumbers();
    this.stampAutoTimestamps();
  }

  getDataPointValues(value: any) {
    let data = [];
    for (let e of value) {
      data.push({
        dataPointId: e["dataPointId"],
        value: "",
        note: "",
      });
    }

    return data;
  }

  customComparator(value1: any, value2: any): boolean | undefined {
    if (typeof value1 === "number" || typeof value2 === "number") {
      return value1 == value2; // Loose equality
    }
    return undefined; // Let _.isEqualWith handle strict equality
  }

  isEnabled() {
    if (this.activeVersion != 0) {
      return false;
    }

    // Check if there are any sections and at least one section is filled
    if (!this.sections || this.sections.length === 0) {
      return false;
    }

    // Disable while guard configuration is still loading
    if (this.guardsLoading) {
      return false;
    }

    // Collect all current section data
    let allCurrentDataPointValues: any = [];
    for (let section of this.sections) {
      allCurrentDataPointValues = [...allCurrentDataPointValues, ...section.dataPointValues];
    }

    // Check if there are changes from the initial state
    const hasChanges = !_.isEqualWith(
      this.initialDataPointValues,
      allCurrentDataPointValues,
      this.customComparator
    );

    if (!hasChanges) {
      return false;
    }

    return true;
  }

  areAllDataPointsValid(): boolean {
    for (let i = 0; i < this.dataPointValues?.length; i++) {
      // Skip if there's a note (notes can substitute for values)
      if (this.dataPointValues[i]["note"]?.length > 0) {
        continue;
      }

      const value = this.dataPointValues[i]["value"];
      const rawValue = value == null ? "" : value.toString();
      const dataType = this.dataPoints[i]["dataType"];
      const isRequired = this.isFieldRequired(this.dataPoints[i]);
      const numerator = dataType === "Fraction" ? this.extractNumerator(rawValue) : "";
      const denominator = dataType === "Fraction" ? this.extractDenominator(rawValue) : "";
      const isFieldEmpty = rawValue.trim() === "";
      const isFractionFullyEmpty = dataType === "Fraction" && numerator === "" && denominator === "";
      const isFractionPartiallyFilled =
        dataType === "Fraction" &&
        (numerator === "" || denominator === "") &&
        !isFractionFullyEmpty;

      if (!isRequired && (isFieldEmpty || isFractionFullyEmpty)) {
        continue;
      }

      // Check if value is empty for required fields
      if (isFieldEmpty || isFractionPartiallyFilled) {
        return false;
      }

      // Check Fraction type has both numerator and denominator
      if (dataType === "Fraction") {
        if (
          parseInt(denominator) === 0
        ) {
          return false;
        }
      }

      // Check Text (Short) type has minimum length
      if (dataType === "Text (Short)" && rawValue.length < 2) {
        return false;
      }

      // Check Text (Long) type has minimum length
      if (dataType === "Text (Long)" && rawValue.length < 2) {
        return false;
      }

      // Check Number type is within range
      if (dataType === "Number") {
        const numValue = parseFloat(rawValue);
        if (
          isNaN(numValue) ||
          numValue < this.dataPoints[i]["dataPointMin"] ||
          numValue > this.dataPoints[i]["dataPointMax"]
        ) {
          return false;
        }
      }
    }

    return true;
  }

  @HostListener("window:beforeunload", ["$event"])
  unloadHandler(event: Event) {
    // if (this.currentRoute.includes('/forms')) {
    //   if (this.hasSubmitted) return;
    //   //Only revert the log sheet status if admin is editing it
    //   this.updateLogsheetStatus(this.logSheet.status, "0");
    // }
    const status = { status: this.logSheet.status, currentUser: "0" };
    const isSuccess = navigator.sendBeacon(
      this.baseUrl + "/logsheet/logs/" + this.logSheet.logSheetId,
      JSON.stringify(status)
    );

    event.returnValue = isSuccess;
  }

  extractNumerator(value: string) {
    if (value == null) {
      return "";
    }
    value = value.toString();
    if (value.length === 0) {
      return "";
    }
    return value.split("/")[0];
  }

  extractDenominator(value: string) {
    if (value == null) {
      return "";
    }
    value = value.toString();
    if (value.length === 0) {
      return "";
    }
    return value.split("/")[1];
  }

  onNumeratorChange(e: Event, idx: number, sectionIdx?: number) {
    const targetSection = sectionIdx !== undefined ? this.sections[sectionIdx] : this;
    const dataPointValues = sectionIdx !== undefined ? targetSection.dataPointValues : this.dataPointValues;

    if (dataPointValues[idx]?.value?.length === 0) {
      dataPointValues[idx].value = `${(e?.target as HTMLInputElement).value
        }/`;
      if (sectionIdx !== undefined) {
        this.onInputChange(sectionIdx);
      }
      return;
    }
    const denominator = this.extractDenominator(
      dataPointValues[idx]?.value
    );
    dataPointValues[idx].value = `${(e.target as HTMLInputElement).value
      }/${denominator}`;
    if (sectionIdx !== undefined) {
      this.onInputChange(sectionIdx);
    }
  }

  onDenominatorChange(e: any, idx: number, sectionIdx?: number) {
    const targetSection = sectionIdx !== undefined ? this.sections[sectionIdx] : this;
    const dataPointValues = sectionIdx !== undefined ? targetSection.dataPointValues : this.dataPointValues;

    if (dataPointValues[idx]?.value?.length === 0) {
      dataPointValues[idx].value = `/${e.target.value}`;
      if (sectionIdx !== undefined) {
        this.onInputChange(sectionIdx);
      }
      return;
    }
    const numerator = this.extractNumerator(dataPointValues[idx]?.value);
    dataPointValues[idx].value = `${numerator}/${e.target.value}`;
    if (sectionIdx !== undefined) {
      this.onInputChange(sectionIdx);
    }
  }

  // updateLogsheetStatus(status: string, currentUser?: string) {
  //   this.logSheetService
  //     .updateLogSheetStatus(this.logSheet.logSheetId, {
  //       status: status,
  //       currentUser,
  //     })
  //     .subscribe({
  //       next: (res) => { },
  //       error: (err) => {
  //         this.errorService.handleError(
  //           err,
  //           "Could not update logsheet status"
  //         );
  //       },
  //     });
  // }

  ngOnDestroy() {
    localStorage.removeItem("token");
    // reverts logsheet status to original state
    if (this.currentRoute.includes('/forms') && !this.hasSubmitted) {
      //Only revert the status when admin is editing it
      // this.updateLogsheetStatus(this.logSheet.status, "0");
    }
  }

  dateTimeFormatter(date: string, idx: number, sectionIdx?: number): void {
    const formattedDate = this.datePipe.transform(date, "MMMM d, y, h:mm a");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      this.onInputChange(sectionIdx);
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
    }
  }

  retrievePreviousVersions(): void {
    this.logSheetFormService
      .retrieveLogSheetVersions(this.logSheet["logSheetId"])
      .subscribe({
        next: (response) => {
          this.versions = response[0]["logSheetTemplated"]["dataPointDetails"];
        },
        error: (err) => {
          this.errorMessage = this.errorService.handleRetrievalErrors(err);
        },
      });
  }

  setActiveVersion(value: number): void {
    this.activeVersion = value;
  }
  toggleVersions(): void {
    this.isVersionMenuVisible = !this.isVersionMenuVisible;
  }

  toggleNoteMenu(idx: number, sectionIdx?: number): void {
    this.selectedDataPointIdx = idx;
    this.selectedSectionIdx = sectionIdx !== undefined ? sectionIdx : 0;
    this.isNoteMenuVisible = !this.isNoteMenuVisible;
  }

  toggleNoteModal(): void {
    this.isNoteMenuVisible = false;
    this.showNoteModal = !this.showNoteModal;
    if (this.showNoteModal) {
      const dataPointValues = this.sections[this.selectedSectionIdx]?.dataPointValues || this.dataPointValues;
      this.note = dataPointValues[this.selectedDataPointIdx]["note"] ?? "";
    } else {
      this.note = "";
    }
  }

  showDeactivateModal(): void {
    this.isNoteMenuVisible = false;
    this.deactivateText.message1 =
      "You are about to delete " +
      this.dataPoints[this.selectedDataPointIdx]["dataPointName"] +
      " Note. This change is irreversible. Do you wish to proceed? ";
    this.showDeactivateMenu = true;
  }

  isNoteSubmitBtnEnabled(): boolean {
    const dataPointValues = this.sections[this.selectedSectionIdx]?.dataPointValues || this.dataPointValues;
    if (
      this.note.trim().length < 2 ||
      (this.isEditing(this.selectedSectionIdx) &&
        this.note == dataPointValues[this.selectedDataPointIdx]["note"])
    ) {
      return false;
    }
    return true;
  }

  isEditing(sectionIdx?: number): boolean {
    const dataPointValues = sectionIdx !== undefined && this.sections[sectionIdx]
      ? this.sections[sectionIdx].dataPointValues
      : this.dataPointValues;

    if (
      dataPointValues[this.selectedDataPointIdx]["note"] == "" ||
      dataPointValues[this.selectedDataPointIdx]["note"] == null
    ) {
      return false;
    }
    return true;
  }

  invalidFields: any = {};

  isEmpty(obj: any) {
    return Object.entries(obj).length === 0;
  }

  hasTrueForDPId(dpId: string): boolean {
    if (this.singleSelect.hasOwnProperty(dpId)) {
      const dpIdValues = Object.values(this.singleSelect[dpId]);
      return dpIdValues.includes(true);
    }
    return false;
  }

  private resolveBooleanFlag(value: any, fallback: boolean): boolean {
    if (value === true || value === "true" || value === 1 || value === "1") {
      return true;
    }
    if (value === false || value === "false" || value === 0 || value === "0") {
      return false;
    }
    return fallback;
  }

  isFieldRequired(dataPoint: any): boolean {
    const isHidden = this.resolveBooleanFlag(dataPoint?.["hiddenByDefault"], false);
    if (isHidden) {
      return false;
    }

    const mandatoryFlag =
      dataPoint?.["mandatoryWhenVisible"] ??
      dataPoint?.["requiredWhenVisible"];

    const conditionRequiredFlag =
      dataPoint?.fieldCondition?.requiredWhenVisible ??
      dataPoint?.triggerConditions?.requiredWhenVisible;

    const isMandatory = this.resolveBooleanFlag(mandatoryFlag, true);
    const isRequiredWhenVisible = this.resolveBooleanFlag(conditionRequiredFlag, false);

    return isMandatory || isRequiredWhenVisible;
  }

  private getFieldValidationError(section: any, sectionIdx: number, fieldIdx: number): string | null {
    const fieldValue = section?.dataPointValues?.[fieldIdx];
    const dataPoint = section?.dataPoints?.[fieldIdx];
    if (!fieldValue || !dataPoint) {
      return null;
    }

    // Auto fields are system-generated/computed and never user-fillable, so an
    // empty one (e.g. a Duration whose referenced date field was deactivated)
    // must never block submission.
    if (['Duration', 'Timestamp (Auto)', 'Reference Number'].includes(dataPoint['dataType'])) {
      return null;
    }

    if ((fieldValue["note"] ?? "").length > 0) {
      return null;
    }

    const value = fieldValue["value"];
    const rawValue = value == null ? "" : value.toString();
    const trimmedValue = rawValue.trim();
    const dataType = dataPoint["dataType"];
    const isRequired = this.isFieldRequired(dataPoint);

    const numerator = dataType === "Fraction" ? this.extractNumerator(rawValue) : "";
    const denominator = dataType === "Fraction" ? this.extractDenominator(rawValue) : "";
    const isFieldEmpty = trimmedValue === "";
    const isFractionFullyEmpty = dataType === "Fraction" && numerator === "" && denominator === "";
    const isFractionPartiallyFilled =
      dataType === "Fraction" &&
      (numerator === "" || denominator === "") &&
      !isFractionFullyEmpty;

    if (!isRequired && (isFieldEmpty || isFractionFullyEmpty)) {
      return null;
    }

    if (isFieldEmpty || isFractionPartiallyFilled) {
      return this.errorMsgEmpty;
    }

    if ((dataType === "Text (Short)" || dataType === "Text (Long)") && rawValue.length < 2) {
      return this.errorMsgMinValue;
    }

    if (dataType === "Number") {
      const parsedNumber = parseFloat(rawValue);
      if (Number.isNaN(parsedNumber)) {
        return this.errorMsgNAN;
      }
      if (
        parsedNumber < dataPoint["dataPointMin"] ||
        parsedNumber > dataPoint["dataPointMax"]
      ) {
        return this.errorMsgRange;
      }
    }

    if (dataType === "Fraction" && parseInt(denominator, 10) === 0) {
      return this.errorMsgDenominator;
    }

    if (dataType === "Date") {
      this.validateManualDateInput(fieldIdx, sectionIdx);
      if (fieldValue["dateFormatError"]) {
        return this.errorMsgInvalidDate;
      }
    }

    return null;
  }

  validateFields(): void {
    let invalidFields: Record<string, string> = {};

    // Validate all sections
    for (let sectionIdx = 0; sectionIdx < this.sections.length; sectionIdx++) {
      const section = this.sections[sectionIdx];
      const sectionDataPointValues = section.dataPointValues;

      for (let i = 0; i < sectionDataPointValues?.length; i++) {
        const fieldKey = `${sectionIdx}_${i}`;
        const error = this.getFieldValidationError(section, sectionIdx, i);
        if (error) {
          invalidFields = { ...invalidFields, [fieldKey]: error };
        }
      }
    }

    this.invalidFields = invalidFields;
  }

  updateNote(): void {
    let isEditingNote = this.isEditing(this.selectedSectionIdx);
    const dataPointValues = this.sections[this.selectedSectionIdx]?.dataPointValues || this.dataPointValues;
    dataPointValues[this.selectedDataPointIdx]["note"] = this.note.trim();
    if (this.selectedSectionIdx !== undefined && this.selectedSectionIdx !== 0) {
      this.onInputChange(this.selectedSectionIdx);
    }
    this.toggleNoteModal();
    this.toastService.showToast = true;
    this.toastService.toastText = isEditingNote ? "Note Edited!" : "Note Added!";
    setTimeout(() => {
      this.toastService.showToast = false;
    }, 5000);
  }

  deleteNote(): void {
    const dataPointValues = this.sections[this.selectedSectionIdx]?.dataPointValues || this.dataPointValues;
    dataPointValues[this.selectedDataPointIdx]["note"] = "";
    if (this.selectedSectionIdx !== undefined && this.selectedSectionIdx !== 0) {
      this.onInputChange(this.selectedSectionIdx);
    }

    this.note = "";
    this.showDeactivateMenu = false;
    this.toastService.showToast = true;
    this.toastService.toastText = "Note Deleted!";
    setTimeout(() => {
      this.toastService.showToast = false;
    }, 5000);
  }

  setCurrentTimestamp(idx: number, sectionIdx?: number): void {
    let date = new Date().toISOString();
    const formattedDate = this.datePipe.transform(date, "MMMM d, y h:mm a");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      this.onInputChange(sectionIdx);
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
    }
  }

  dateFormatter(date: string, idx: number, sectionIdx?: number): void {
    // Validate date before formatting
    if (!date || !this.isValidDate(date)) {
      return;
    }
    const formattedDate = this.datePipe.transform(date, "dd-MMM-yyyy");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      this.onInputChange(sectionIdx);
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
    }
  }

  // Validates if a date string is valid
  isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  setCurrentDate(idx: number, sectionIdx?: number): void {
    let date = new Date().toISOString();
    const formattedDate = this.datePipe.transform(date, "dd-MMM-yyyy");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      this.sections[sectionIdx].dataPointValues[idx]["dateFormatError"] = false;
      this.onInputChange(sectionIdx);
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
      this.dataPointValues[idx]["dateFormatError"] = false;
    }
  }

  // Validate manual date input in dd-MMM-yyyy format
  validateManualDateInput(idx: number, sectionIdx?: number): void {
    const dataPointValues = sectionIdx !== undefined
      ? this.sections[sectionIdx].dataPointValues
      : this.dataPointValues;

    const value = dataPointValues[idx]["value"];

    // Allow empty values
    if (!value || value.trim() === "") {
      dataPointValues[idx]["dateFormatError"] = false;
      return;
    }

    // Check if the format matches dd-MMM-yyyy (e.g., 29-Jan-2026)
    const datePattern = /^(\d{1,2})-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(\d{4})$/i;
    const match = value.match(datePattern);

    if (!match) {
      dataPointValues[idx]["dateFormatError"] = true;
      return;
    }

    // Validate the date is actually valid
    const day = parseInt(match[1], 10);
    const monthStr = match[2];
    const year = parseInt(match[3], 10);

    // Validate year is in reasonable range (1900-2100)
    if (year < 1900 || year > 2100) {
      dataPointValues[idx]["dateFormatError"] = true;
      return;
    }

    const months: { [key: string]: number } = {
      'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
      'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
    };

    const month = months[monthStr.toLowerCase()];
    const testDate = new Date(year, month, day);

    // Check if the date is valid (handles invalid days like Feb 30)
    if (testDate.getFullYear() !== year ||
      testDate.getMonth() !== month ||
      testDate.getDate() !== day) {
      dataPointValues[idx]["dateFormatError"] = true;
      return;
    }

    // Format is valid - normalize the format (capitalize month abbreviation)
    const formattedDate = this.datePipe.transform(testDate, "dd-MMM-yyyy");
    dataPointValues[idx]["value"] = formattedDate;
    dataPointValues[idx]["dateFormatError"] = false;

    if (sectionIdx !== undefined) {
      this.onInputChange(sectionIdx);
    }
  }

  // Handle date picker selection
  onDatePickerChange(event: any, idx: number, sectionIdx?: number): void {
    if (!event || !event.value) return;

    const formattedDate = this.datePipe.transform(event.value, "dd-MMM-yyyy");
    if (sectionIdx !== undefined) {
      this.sections[sectionIdx].dataPointValues[idx]["value"] = formattedDate;
      this.sections[sectionIdx].dataPointValues[idx]["dateFormatError"] = false;
      this.onInputChange(sectionIdx);
    } else {
      this.dataPointValues[idx]["value"] = formattedDate;
      this.dataPointValues[idx]["dateFormatError"] = false;
    }
  }

  printPage() {
    window.print();
  }

  initializeSections(): void {
    // Initialize with one empty section (only called when no existing data)
    this.sections = [
      {
        id: 1,
        name: "Entry 1",
        dataPointValues: JSON.parse(JSON.stringify(this.dataPointValues)),
        dataPoints: this.dataPoints,
        isSubmitted: false,
        entryId: 1,
      },
    ];
    this.sectionCount = 1;
    this.submittedSectionCount = 0;
    this.nextEntryId = 2;
  }

  addSection(): void {
    if (!this.isDynamicForm || this.isAddingSection) {
      return;
    }
    this.isAddingSection = true;
    // Always allow adding a new section after submission
    this.sectionCount++;
    const newSection = {
      id: this.sectionCount,
      name: `Entry ${this.sectionCount}`,
      dataPointValues: this.getDataPointValues(this.dataPoints),
      dataPoints: this.dataPoints,
      isSubmitted: false,
      entryId: this.nextEntryId,
    };
    // Add new section at the end of the array
    this.sections.push(newSection);
    this.nextEntryId++;

    // Scroll to the new section after a short delay to allow rendering
    setTimeout(() => {
      this.scrollToSection(this.sectionCount);
      this.isAddingSection = false;
    }, 100);
  }

  scrollToSection(sectionId: number): void {
    const sectionIndex = this.sections.findIndex((s: any) => s.id === sectionId);
    if (sectionIndex !== -1) {
      const sectionElements = document.querySelectorAll('.form-section');
      if (sectionElements[sectionIndex]) {
        sectionElements[sectionIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  removeSection(sectionId: number): void {
    if (!this.isDynamicForm || this.isDeleting) {
      return;
    }
    this.isDeleting = true;
    const sectionToRemove = this.sections.find((s: any) => s.id === sectionId);
    if (this.sections.length > 1 && sectionToRemove) {
      // For dynamic forms, track the entry ID for deletion if it exists and is a real database ID
      if (this.isDynamicForm && sectionToRemove.entryId && typeof sectionToRemove.entryId === 'number') {
        this.deletedEntryIds.push(sectionToRemove.entryId);
      }

      // Remove the section from the array
      this.sections = this.sections.filter(
        (section: any) => section.id !== sectionId
      );
    }
    this.isDeleting = false;
  }

  isSectionValid(section: any): boolean {
    const sectionIdx = this.sections.findIndex((s: any) => s.id === section?.id);
    const resolvedSectionIdx = sectionIdx >= 0 ? sectionIdx : 0;
    for (let i = 0; i < section.dataPointValues?.length; i++) {
      if (this.getFieldValidationError(section, resolvedSectionIdx, i)) {
        return false;
      }
    }
    return true;
  }

  hasIncompleteUnsubmittedSections(): boolean {
    // Check if there are any unsubmitted sections that are not valid
    return this.sections.some((section: any) =>
      !section.isSubmitted && !this.isSectionValid(section)
    );
  }

  getDataPointIndexForSection(sectionIdx: number, dpIdx: number): number {
    // Helper method to calculate the global data point index
    return sectionIdx * this.dataPoints.length + dpIdx;
  }

  private getAllDataPointValues(): { dataPointId: number; value: string }[] {
    const result: { dataPointId: number; value: string }[] = [];
    for (const section of this.sections) {
      for (const dpv of section.dataPointValues) {
        result.push({ dataPointId: dpv.dataPointId, value: dpv.value ?? '' });
      }
    }
    return result;
  }

  private runGuardPipeline(queue: SubmissionGuard[], onAllCleared: () => void): void {
    if (queue.length === 0) {
      onAllCleared();
      return;
    }
    this.guardQueue = queue;
    const [current, ...rest] = queue;
    const allValues = this.getAllDataPointValues();
    this.activeGuard = current;
    this.activeGuardMessage = this.guardService.interpolateMessage(current.warningMessage, current, allValues);
    this.pendingSubmitCallback = () => {
      if (current.guardType === 'Acknowledged') {
        const templateId = this.logSheet?.logSheetTemplated?.logSheetTemplateId ?? this.logSheet?.logSheetTemplateId;
        this.guardService.saveAcknowledgment(this.logSheet['logSheetId'], templateId, current.id).subscribe();
      }
      this.activeGuard = null;
      setTimeout(() => this.runGuardPipeline(rest, onAllCleared));
    };
  }

  onGuardProceed(): void {
    if (this.pendingSubmitCallback) {
      this.pendingSubmitCallback();
      this.pendingSubmitCallback = null;
    }
  }

  onGuardGoBack(): void {
    this.activeGuard = null;
    this.pendingSubmitCallback = null;
    this.guardQueue = [];
    this.isSubmitting = false;
  }

  // Auto-stamp "Submitted At" timestamp fields with the current time on submit.
  private stampAutoTimestamps(): void {
    const now = new Date().toISOString();
    for (const section of this.sections) {
      if (section.isSubmitted) continue;
      (section.dataPoints || []).forEach((dp: any, i: number) => {
        if (dp['dataType'] === 'Timestamp (Auto)' && section.dataPointValues[i]) {
          section.dataPointValues[i]['value'] = now;
        }
      });
    }
  }

  // Generate a unique reference for empty "Reference Number" fields on submit.
  // Config is stored in listOptions as "prefix,padding".
  private stampReferenceNumbers(): void {
    for (const section of this.sections) {
      if (section.isSubmitted) continue;
      (section.dataPoints || []).forEach((dp: any, i: number) => {
        const cell = section.dataPointValues[i];
        if (dp['dataType'] === 'Reference Number' && cell && !cell['value']) {
          const cfg = (dp['listOptions'] || 'REF-,4').split(',');
          const prefix = cfg[0] ?? 'REF-';
          const padding = Math.max(1, parseInt(cfg[1], 10) || 4);
          const num = String(Date.now() % Math.pow(10, padding)).padStart(padding, '0');
          cell['value'] = `${prefix}${num}`;
        }
      });
    }
  }


  saveLogSheet(isSaving: boolean): void {
    this.isSaving = isSaving;
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    this.stampAutoTimestamps();
    this.stampReferenceNumbers();
    if (this.currentRoute.includes('/forms')) {
      this.validateFields();
    }
    let unsavedSections = this.sections.filter((section: any) => !section.isSubmitted);

    if (Object.keys(this.invalidFields).length == 0 && (unsavedSections.length > 0 || this.deletedEntryIds.length > 0)) {
      if (!isSaving && this.guards.length > 0) {
        // Evaluate guards for each unsaved section independently so that a
        // guard fires when ANY section being submitted meets the condition —
        // not only when the first loaded section happens to meet it (which
        // would silently skip guards on standard/static forms where all
        // sections start as isSubmitted:false).
        const triggeredMap = new Map<number, SubmissionGuard>();
        for (const section of unsavedSections) {
          const sectionValues: { dataPointId: number; value: string }[] =
            section.dataPointValues.map((dpv: any) => ({
              dataPointId: dpv.dataPointId,
              value: dpv.value ?? '',
            }));
          const sectionTriggered = this.guardService.getTriggeredGuards(this.guards, sectionValues);
          for (const g of sectionTriggered) {
            if (!triggeredMap.has(g.id)) {
              triggeredMap.set(g.id, g);
            }
          }
        }
        const triggered = Array.from(triggeredMap.values()).sort(
          (a, b) => a.priority - b.priority
        );
        if (triggered.length > 0) {
          this.runGuardPipeline(triggered, () => this.performSubmit(unsavedSections));
          return;
        }
      }
      this.performSubmit(unsavedSections);
    } else {
      this.isSubmitting = false;
    }
  }

  private performSubmit(unsavedSections: any[]): void {
    const deleteRequests = this.deletedEntryIds.map((entryId) => {
      return this.logSheetFormService
        .deleteEntry(this.logSheet["logSheetId"], entryId)
        .toPromise()
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.error(`Failed to delete entry ${entryId}:`, error);
          return null;
        });
    });

    Promise.all(deleteRequests).then(() => {

      this.logSheetFormService
        .saveMultipleTempEntries(this.logSheet["logSheetId"], unsavedSections, false)
        .toPromise()
        .then((response) => {
          this.deletedEntryIds = [];

          for (let section of unsavedSections) {
            section.isSubmitted = true;
            this.submittedSectionCount++;
          }

          this.nextEntryId = this.submittedSectionCount + 1;

          let allDataPointValues: any = [];
          for (let section of this.sections) {
            allDataPointValues = [...allDataPointValues, ...section.dataPointValues];
          }
          this.initialDataPointValues = JSON.parse(JSON.stringify(allDataPointValues));
          this.dataPointValues = allDataPointValues;

          this.invalidFields = {};

          if (this.currentRoute.includes('/forms')) {
            this.hasSubmitted = true;
          }
          this.logSheet = null;
        })
        .catch((error) => {
          this.isSubmitting = false;
          this.errorService.handleError(error, "Error submitting logsheet changes");
        });
    });
  }
}
